import { createSeededRandom } from '../utils/random';

/** What the phone-scare director needs to know each frame. */
export type ScareFrame = {
  elapsed: number;
  night: number;
  /** A night he doesn't come: no phone scares at all. */
  calm: boolean;
  /** Free walking (not unlocking, not a glance or startle, not mid-scare). */
  free: boolean;
  tension: number;
  herSpeed: number;
  lookingBack: boolean;
  mapOpen: boolean;
  chatOpen: boolean;
  flashlight: boolean;
  /** She is in the dark without a light. */
  dark: boolean;
  himPresent: boolean;
  himVisible: boolean;
  /** She has noticed him at least once tonight. */
  seenHim: boolean;
  /** The player's own phone is linked (the only place the texts go). */
  linked: boolean;
};

export type ScareAction =
  /** A text from nobody, addressed to whoever is holding the phone (linked phones only). */
  | { kind: 'text'; text: string }
  /** Unknown is video-calling. It answers itself. */
  | { kind: 'call' }
  /** His face for a split second over the map. */
  | { kind: 'flash' };

/**
 * Decides when her phone turns on her: texts from an unknown sender that know
 * what she just did (night 3 on), a video call that picks itself up (night 4
 * on), and his face flickering over the map (night 5 on). Never on the quiet
 * nights, never two at once, and never anything that changes the chase.
 */
export class PhoneScares {
  private readonly rng = createSeededRandom(4417);
  private readonly used = new Set<string>();
  private night = 1;
  private calm = true;
  private lastScare = -99;
  private texts = 0;
  private lastText = -99;
  private callAt = 0;
  private called = false;
  private flashed = false;
  private prevSpeed = 0;
  private wasLooking = false;
  private lookSawHim = false;
  private lookTime = 0;
  private lightTime = 0;
  private darkTime = 0;
  private mapTime = 0;

  setNight(night: number, calm: boolean): void {
    this.night = night;
    this.calm = calm;
    this.reset();
  }

  reset(): void {
    this.lastScare = -99;
    this.texts = 0;
    this.lastText = -99;
    this.callAt = CALL_EARLIEST + this.rng() * CALL_WINDOW;
    this.called = false;
    this.flashed = false;
    this.prevSpeed = 0;
    this.wasLooking = false;
    this.lookSawHim = false;
    this.lookTime = 0;
    this.lightTime = 0;
    this.darkTime = 0;
    this.mapTime = 0;
  }

  /** Dev: make each scare due now. */
  hurry(): void {
    this.callAt = 0;
    this.lastScare = -99;
    this.lastText = -99;
    this.texts = 0;
    this.called = false;
    this.flashed = false;
  }

  update(f: ScareFrame, dt: number): ScareAction | null {
    const stopped = this.prevSpeed > 0.8 && f.herSpeed < 0.15;
    this.prevSpeed = f.herSpeed;
    // She turned round and he wasn't there.
    let lookedAway = false;
    if (f.lookingBack) {
      this.lookTime += dt;
      if (f.himVisible) this.lookSawHim = true;
    } else if (this.wasLooking) {
      lookedAway = this.lookTime > 0.5 && !this.lookSawHim;
      this.lookTime = 0;
      this.lookSawHim = false;
    }
    this.wasLooking = f.lookingBack;
    this.lightTime = f.flashlight ? this.lightTime + dt : 0;
    this.darkTime = f.dark && !f.flashlight ? this.darkTime + dt : 0;
    this.mapTime = f.mapOpen ? this.mapTime + dt : 0;

    if (this.calm || this.night < 4 || !f.free || f.elapsed - this.lastScare < SCARE_GAP) return null;

    // His face over the map: the first time she holds it up a while, late enough in the walk.
    if (this.night >= 6 && !this.flashed && f.mapOpen && this.mapTime > 1.1 && f.elapsed > 40 && f.himPresent) {
      this.flashed = true;
      return this.fire(f, { kind: 'flash' });
    }

    // The call: once a night, while he's out of sight and she's uneasy but not yet running for her life.
    if (
      this.night >= 5 &&
      !this.called &&
      f.elapsed > this.callAt &&
      f.himPresent &&
      f.seenHim &&
      !f.himVisible &&
      !f.mapOpen &&
      !f.chatOpen &&
      f.tension > 0.2 &&
      f.tension < 0.85
    ) {
      this.called = true;
      return this.fire(f, { kind: 'call' });
    }

    // Texts: only once she knows he's out there, and only about something she just did.
    const maxTexts = this.night >= 7 ? 3 : 2;
    if (!f.linked || !f.seenHim || this.texts >= maxTexts || f.elapsed < TEXT_EARLIEST || f.elapsed - this.lastText < TEXT_GAP || f.chatOpen) return null;
    let pool: readonly string[] | null = null;
    if (stopped && !f.himVisible) pool = STOPPED;
    else if (lookedAway) pool = LOOKED;
    else if (this.lightTime > 6) pool = LIGHT;
    else if (this.darkTime > 8) pool = DARK;
    else if (this.mapTime > 2.5 && this.night < 6) pool = MAP;
    else if (f.elapsed - this.lastText > TEXT_IDLE && f.elapsed > TEXT_IDLE) pool = this.generic();
    if (!pool) return null;
    const text = this.pick(pool);
    if (!text) return null;
    this.texts += 1;
    this.lastText = f.elapsed;
    return this.fire(f, { kind: 'text', text });
  }

  private fire(f: ScareFrame, action: ScareAction): ScareAction {
    this.lastScare = f.elapsed;
    return action;
  }

  private generic(): readonly string[] {
    const lines = [...GENERIC];
    if (this.night >= 5) lines.push('did you like the keychain? I kept it safe for you.');
    if (this.night >= 7) lines.push('I know which building is yours.', 'third floor. the light by the stairs is broken.');
    return lines;
  }

  /** A line not yet sent this session (they repeat only once every one is used). */
  private pick(pool: readonly string[]): string | null {
    let fresh = pool.filter((t) => !this.used.has(t));
    if (fresh.length === 0) {
      for (const t of pool) this.used.delete(t);
      fresh = [...pool];
    }
    const text = fresh[Math.floor(this.rng() * fresh.length)];
    this.used.add(text);
    return text ?? null;
  }
}

const STOPPED = ['why did you stop?', 'keep walking.', "don't stop. I'm right here.", 'you stopped. so did I.'];
const LOOKED = ["I'm not there.", 'wrong side.', 'you keep looking in the wrong place.', 'closer than that.'];
const LIGHT = ['I can see your light from here.', 'nice light. easy to follow.'];
const DARK = ["it's dark. I can still see you.", 'you think the dark hides you?'];
const MAP = ["the map won't help you.", "that's not the way home. I know the way home."];
const GENERIC = ["you walk faster when you're scared.", 'your glasses suit you.', 'almost home.', 'you look tired tonight.'];

/** Seconds between any two phone scares. */
const SCARE_GAP = 25;
/** Texts: not before this far into the walk (s), at least this far apart, and one anyway after this long without. */
const TEXT_EARLIEST = 45;
const TEXT_GAP = 60;
const TEXT_IDLE = 150;
/** The call comes somewhere in [CALL_EARLIEST, CALL_EARLIEST + CALL_WINDOW] seconds in (when he's around). */
const CALL_EARLIEST = 75;
const CALL_WINDOW = 110;
